import React from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../../store/reducer';
import { Box, Button, Stack, Typography, useTheme, alpha, IconButton, Slider } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { getOperatorPayout, getPickemCalculatedData } from './optimizerHelper';
import { addLineupEntry, removeLineupEntry, removePickFromEntry, setCurrentEntry, setIsEntryView, setLeagueFilter, setSelectedEntry } from './store/slice';
import { IParlayOutcome, IShopOutcome, Leagues, Operators, OptimizerEntry, Payout, PickemParlayType, Timeframe } from './store/types';
import { APIConstants } from '../utils/api/constants';

const EntryOutcome = (props: { outcome: IShopOutcome }) => {
  const { outcome } = props;
  const theme = useTheme();
  const dispatch = useDispatch();

  return (
      <Box sx={{ boxSizing: 'border-box', width: '100%', height: 'px', padding: '10px 10px', background: 'rgba(10, 10, 10, 0.50)', border: '1px rgba(255, 255, 255, 0.05) solid', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Box sx={{ background: alpha(theme.palette.common.white, 0.15), borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', width: '50px', height: '50px' }}>
              <img style={{ width: '100%', height: '100%', borderRadius: '50%' }} src={outcome.playerParticipantHeadShot} alt="profile" />
          </Box>
          <Box sx={{ flex: '1', display: 'flex', flexDirection: 'column', gap: '1px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Typography sx={{ color: theme.palette.common.white, fontWeight: '500' }}>{outcome.participantLabel}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {outcome.injuryStatus &&
                          <Box sx={{ background: '#FBFF39', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px' }}>
                              <Typography sx={{ color: 'black', fontSize: '16px', fontWeight: '400' }}>Q</Typography>
                          </Box>}
                  </Box>
              </Box>
              <Typography sx={{ color: theme.palette.common.white, fontSize: '14px', fontWeight: '700' }}>{outcome.line} {outcome.offerName}</Typography>
              <Typography sx={{ color: alpha(theme.palette.common.white, 0.7), fontSize: '10px', fontWeight: '400' }}>{outcome.eventLabel}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              {/*
              add back for v2
              <Box sx={{ background: 'rgba(255, 107, 0, 0.10)', borderRadius: '6px', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '0.50px rgba(255, 107, 0, 0.70) solid', width: '32px', height: '32px' }}>
                  <InsertChartIcon sx={{ color: '#FF6B00' }} />
                      </Box>*/}
              <Box style={{
                  padding: '4px', background: outcome.label === 'Over' ? '#015ACE' : alpha(theme.palette.common.white, 0.10),
                  borderRadius: '6px', display: 'inline-flex'
              }}>
                  <ArrowUpwardIcon sx={{ color: 'white', }}/>
              </Box>
              <Box style={{ padding: '4px', background: outcome.label === 'Under' ? '#015ACE' : alpha(theme.palette.common.white, 0.10), borderRadius: '6px', display: 'inline-flex' }}>
                  <ArrowDownwardIcon sx={{ color: 'white', }}/>
              </Box>
              {<IconButton onClick={() => dispatch(removePickFromEntry(outcome))}>
                  <CloseIcon sx={{ color: alpha(theme.palette.common.white, 0.7) }} />
              </IconButton>}
          </Box>
      </Box>
  );
};

const BlankPlaceholder = () => {
  const theme = useTheme();
  return (
      <Box sx={{
          alignSelf: 'stretch',
          padding: '10px 10px',
          background: 'rgba(10, 10, 10, 0.50)',
          border: `1px ${alpha(theme.palette.common.white, 0.05)} solid`,
          alignItems: 'center',
          gap: '10px',
          display: 'flex',
      }}>
          <Box sx={{
              width: '50px',
              height: '50px',
              background: alpha(theme.palette.common.white, 0.15),
              borderRadius: '50%',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
          }}>
              <AddIcon sx={{ color: 'white', }}/>
          </Box>
          <Box
              sx={{
                  flex: '1 1 0',
                  flexDirection: 'column',
                  gap: '4px',
              }}>
              <Typography
                  sx={{
                      color: theme.palette.common.white,
                      fontSize: '16px',
                      fontWeight: '500',
                  }}>
                  Add a Pick
              </Typography>
              <Typography
                  sx={{
                      color: alpha(theme.palette.common.white, 0.70),
                      fontSize: '12px',
                      fontWeight: '400',
                  }}>
                  Minimum 2 Picks to play Pick6.
              </Typography>
          </Box>
          <Box sx={{ gap: '5px', display: 'flex', }}>
              <Box sx={{ padding: '4px', background: alpha(theme.palette.common.white, .10), borderRadius: '6px', display: 'inline-flex' }}>
                  <ArrowUpwardIcon sx={{ color: 'white',}} />
              </Box>
              <Box sx={{ padding: '4px', background: alpha(theme.palette.common.white, .10), borderRadius: '6px', display: 'inline-flex' }}>
                  <ArrowDownwardIcon sx={{ color: 'white', }} />
              </Box>
              <CloseIcon sx={{ alignSelf: 'center', color: alpha(theme.palette.common.white, 0.1) }} />
          </Box>
      </Box>
  );
}

const EntryLineup = (props: { entry: OptimizerEntry }) => {
  const { entry } = props;
  const { wager, optimizerState: { selectedEntry, optimizerData } } = useSelector((state: IRootState) => state.pick6);
  const theme = useTheme();
  const dispatch = useDispatch();
  const displayData: {
      oddsAmerican: string;
      expectedWinPct: number;
      expectedRoiPct: number;
  } = getPickemCalculatedData(entry.outcomes as IParlayOutcome[] ?? [], Operators.PICK6, PickemParlayType.FLEX);
  const league = entry?.outcomes?.[0]?.leagueCode;
  const payout = getOperatorPayout(entry.outcomes?.length, optimizerData.filter(x => x.leagueCode === league)[0]);

  const handleEntryDelete = (e: React.MouseEvent<HTMLButtonElement>, entry: OptimizerEntry) => {
      e.stopPropagation(); // Prevent the event from bubbling up
      dispatch(removeLineupEntry(entry));
      if (selectedEntry === entry.id) {
          dispatch(setSelectedEntry(0));
      }
  };

  return (
      <Box key={entry?.id}
          onClick={() => {
                  dispatch(setSelectedEntry(entry.id)); 
                  dispatch(setIsEntryView(true));
              }}
          sx={{
              '&:hover': {
                  background: alpha(theme.palette.primary.dark, 0.05)
              },
              width: '100%', height: '92px', padding: '10px',
              boxSizing: 'border-box',
              borderBottom: `1px ${alpha(theme.palette.common.white, 0.15)} solid`,
              flexDirection: 'column', gap: '10px', display: 'inline-flex', backgroundColor: entry?.id === selectedEntry ? alpha(theme.palette.primary.dark, 0.15) : ''
          }}>
          <Box sx={{ justifyContent: 'space-between', display: 'inline-flex' }}>
              <Box sx={{ paddingRight: '12px', gap: '15px', display: 'flex' }}>
                  <Typography style={{ color: theme.palette.common.white, fontSize: '18px', fontWeight: '700', height: '24px' }}>Lineup {entry?.id + 1}</Typography>
                  {entry?.outcomes && entry?.outcomes[0] && (
                      <img src={`https://awesemostorage.blob.core.windows.net/assets/leagues/${league.toLowerCase()}.png`} alt="" style={{ height: '25px' }} />
                  )}
              </Box>
              <Box sx={{ alignItems: 'center', gap: '10px', display: 'flex' }}>
                  <Box sx={{ alignItems: 'center', display: 'flex' }}>
                      {entry?.outcomes?.map((leg, index) => (
                          <img src={leg.playerParticipantHeadShot} alt="" style={{ width: '25px', height: '25px', borderRadius: '20px', backgroundColor: '#004094', border: '1px #4395FF solid', marginLeft: '-8px' }} />
                      ))}
                      {entry?.outcomes && entry?.outcomes.length < 6 && [...Array(6 - entry.outcomes.length)].map((_, i) => {
                          return (<img src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" alt="" style={{ borderColor: alpha(theme.palette.common.white, 0.70), border: '0.50px dotted', marginLeft: '-8px', width: '25px', height: '25px', borderRadius: '20px', backgroundColor: '#1B1E25' }} />);
                      })}
                  </Box>
                  <Typography sx={{ color: theme.palette.common.white, fontSize: '14px', fontWeight: '400' }}>{entry?.outcomes?.length ?? 0} PICKS</Typography>
                  <IconButton onClick={(e) => handleEntryDelete(e, entry)}>
                      <CloseIcon sx={{ color: alpha(theme.palette.common.white, 0.7) }} />
                  </IconButton>
              </Box>
          </Box>
          <Box sx={{ gap: '20px', display: 'inline-flex' }}>
              {!entry?.outcomes || entry?.outcomes.length < 2 ?
                  <Typography sx={{ color: '#FCFF6A', fontSize: '12px', fontWeight: '400' }}>Minimum 2 picks to play!</Typography>
                  :
                  <>
                      <Box sx={{ alignItems: 'center', gap: '6px', display: 'flex' }}>
                          <Typography sx={{ color: alpha(theme.palette.common.white, 0.87), fontSize: '12px', fontWeight: '400' }}>xWIN:</Typography>
                          <Typography sx={{ color: theme.palette.common.white, fontSize: '18px', fontWeight: '500' }}>{entry?.outcomes && Math.round(displayData.expectedWinPct)}%</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ color: alpha(theme.palette.common.white, 0.87), fontSize: '12px', fontWeight: '400' }}>Odds:</Typography>
                          <Typography sx={{ color: theme.palette.common.white, fontSize: '18px', fontWeight: '500' }}>{displayData.oddsAmerican}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography sx={{ color: alpha(theme.palette.common.white, 0.87), fontSize: '12px', fontWeight: '400', marginRight: '5px' }}>{payout?.numberOfPicksCorrect}/{payout?.pickSetSize} Correct: </Typography>
                          <Typography sx={{ color: '#00CF6B', fontSize: '14px', fontWeight: '700' }}> {(payout?.lowPayoutMultiplier * wager).toFixed(2)}-{(payout?.highPayoutMultiplier * wager).toFixed(2)}</Typography>
                      </Box>
                  </>
              }
          </Box>
      </Box>
  );
};

const EstimatedPayouts = (props: { payout: Payout, wager: number }) => {
  const { payout, wager } = props;
  const marks = [
      { value: payout.lowPayoutMultiplier * wager, label: '$' + (payout.lowPayoutMultiplier * wager).toFixed(2) },
      { value: payout.averagePayoutMultiplier * wager, label: '$' + (payout.averagePayoutMultiplier * wager).toFixed(2) },
      { value: payout.highPayoutMultiplier * wager, label: '$' + (payout.highPayoutMultiplier * wager).toFixed(2) },
  ];
  return (
      <Box sx={{ padding: '10px', color: 'white', }}>
          <Typography>Estimated Prizing Range</Typography>
          {!payout.numberOfPicksCorrect ?
              <Box><Typography sx={{ fontSize: '10px' }}>Add a minimum of 2 Picks to see your estimated prizing range.</Typography>
              </Box> :
              <Stack sx={{ alignContent: 'center' }} direction='row' gap={1}>
                  <Typography sx={{ fontSize: 12, width: '90px', paddingTop: '5px' }} noWrap>{payout.numberOfPicksCorrect}/{payout.pickSetSize} Correct</Typography>
                  <Slider
                      marks={marks}
                      step={1}
                      value={marks[1].value}
                      min={payout.lowPayoutMultiplier * wager}
                      max={payout.highPayoutMultiplier * wager + payout.lowPayoutMultiplier * wager}
                      disabled={true}
                      sx={{
                          '& .MuiSlider-thumb': {
                              display: 'none',
                          },
                          '& .MuiSlider-rail': {
                              backgroundColor: '#00D809',
                              opacity: 1,
                          },
                          '& .MuiSlider-track': {
                              backgroundColor: '#006405',
                              opacity: 1,
                              border: 'none'
                          },
                          '& .MuiSlider-markLabel': {
                            color: 'white',
                          },
                          '& span:nth-child(6).MuiSlider-markLabel': {
                              marginLeft: '20px'
                          },
                      }}
                  />
              </Stack>}
      </Box>
  );
}

const NewEntryButton = (props: { onClick: () => void }) => {
  const { onClick } = props;
  const theme = useTheme();
  return (
      <a onClick={onClick}>
          <Box sx={{ boxSizing: 'border-box', width: '100%', padding: '10px', borderBottom: `1px ${alpha(theme.palette.common.white, 0.15)} solid`, flexDirection: 'column', gap: '10px', display: 'inline-flex' }}>
              <Box sx={{ justifyContent: 'space-between', display: 'inline-flex' }}>
                  <Box sx={{ paddingRight: '12px', gap: '15px', display: 'flex' }}>
                      <Typography sx={{ cursor: 'pointer', color: theme.palette.common.white, fontSize: '18px', fontWeight: '300' }}>Create New Lineup +</Typography>
                  </Box>
                  <Box sx={{ alignItems: 'center', gap: '10px', display: 'flex' }}>
                      <Box sx={{ alignItems: 'center', display: 'flex' }}>
                          {[...Array(6)].map((_, i) => {
                              return (<img src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" alt="" style={{ border: `0.50px ${alpha(theme.palette.common.white, 0.70)} dotted`, marginLeft: '-8px', width: '25px', height: '25px', borderRadius: '20px', backgroundColor: '#1B1E25' }} />);
                          })}
                      </Box>
                      <Typography sx={{ color: theme.palette.common.white, fontSize: '14px', fontWeight: '400' }}>0 PICKS</Typography>
                  </Box>
              </Box>
          </Box>
      </a>
  );
}

const GenerateLineup = () => {
  const { operator, filters, optimizerState: { entries, selectedDraftGroup, maxLegs } } = useSelector((state: IRootState) => state.pick6);
  const { authenticated } = useSelector((state: IRootState) => state.common);
  const userAuthenticated =  authenticated.status === APIConstants.SUCCEEDED && authenticated.data.result;

  const dispatch = useDispatch();
  const theme = useTheme();

  const GetGeneratedLineup = async () => {
      if (!userAuthenticated) { return; }
      //Get optimizer data for selected league
      //BDK FUTURE Update this to use RDK Query
      const url = `https://oddsshopper-ui.vercel.app/api/parlays/parlay?league=${filters.league as string}&sportsbookEventIds=${selectedDraftGroup?.competitionIds?.join(',') ?? ''}&operator=${operator?.toLowerCase()}&timeframe=${Timeframe.FUTURE_7_DAYS}&legs=${maxLegs}}`;
      const response = await fetch(url, {
          method: 'GET',
          headers: {
              'Content-Type': 'application/json',
          },
      });
      if (response.ok) {
          const data = await response.json();
          //TODO move all new lineup logic into store slice
          let newId = entries.reduce((maxId, item) => (item.id > maxId ? item.id : maxId), entries[0]?.id || 0);
          newId = entries.length >0 ? newId + 1 : 0;
          let genEntry = { id: newId, outcomes: data } as OptimizerEntry;
          dispatch(addLineupEntry(genEntry));
          dispatch(setSelectedEntry(genEntry.id));
          dispatch(setIsEntryView(true));;
      } else {
          console.error('Failed to generate a fantasy optimizer lineup');
      }
  }
  return (
      <Box style={{ boxSizing: 'border-box', width: '100%', height: 'auto', padding: '12px 15px', background: `linear-gradient(180deg, rgba(43, 48, 62, 0) 0%, ${alpha(theme.palette.primary.dark, 0.2)} 100%)`, boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)', justifyContent: 'space-between', alignItems: 'flex-start', display: 'flex', gap: '10px' }}>
          <Box style={{ flex: '1 1 0', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 6, display: 'flex' }}>
              <Box style={{ alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 4, display: 'flex' }}>
                  <Box style={{ alignSelf: 'stretch', color: 'white', fontSize: 16, fontFamily: 'Inter', fontWeight: '700', wordWrap: 'break-word' }}>Generate an Optimized Lineup!</Box>
                  <Box style={{ alignSelf: 'stretch', color: alpha(theme.palette.common.white, 0.87), fontSize: 12, fontFamily: 'Inter', fontWeight: '500', wordWrap: 'break-word' }}>To get started, use this button to generate a lineup optimized by Stokastic, or use the table to the right to manually add picks.</Box>
              </Box>
          </Box>
          <Box style={{ alignSelf: 'stretch', justifyContent: 'flex-end', alignItems: 'center', display: 'flex' }}>
              <Button onClick={GetGeneratedLineup} style={{ color: 'white', background: alpha(theme.palette.primary.dark, 0.2), borderRadius: 6, border: '0.50px solid ' + alpha(theme.palette.primary.main, 0.6) }}>
                  Generate
              </Button>
          </Box>
      </Box>
  );
}

const LineupSelector = () => {
  const { wager, filters, optimizerState: { entries, selectedEntry, isEntryView, optimizerData } } = useSelector((state: IRootState) => state.pick6);
  const theme = useTheme();
  const dispatch = useDispatch();

  let currentEntry = entries.find(x => x.id === selectedEntry) ?? entries[0];
  const league = currentEntry?.outcomes?.[0]?.leagueCode;
  const payout = getOperatorPayout(currentEntry?.outcomes?.length, optimizerData.filter(x => x.leagueCode === league)[0]);

  const newLineupClick = () => {
    let newId = entries.reduce((maxId, item) => (item.id > maxId ? item.id : maxId), entries[0]?.id || 0);
    newId = entries.length > 0 ? newId + 1 : 0;
    let blankEntry = { id: newId, outcomes: [] } as OptimizerEntry;
    dispatch(addLineupEntry(blankEntry));
    dispatch(setSelectedEntry(blankEntry.id));
    dispatch(setIsEntryView(true));
  };

  const toggleView = () => {
    dispatch(setIsEntryView(!isEntryView));
  };
  const clearLineup = () => {
    dispatch(setCurrentEntry({ id: selectedEntry } as OptimizerEntry))
  };
  const displayData: {
    oddsAmerican: string;
    expectedWinPct: number;
    expectedRoiPct: number;
  } = getPickemCalculatedData(currentEntry?.outcomes as IParlayOutcome[] ?? [], Operators.PICK6, PickemParlayType.FLEX);

  React.useEffect(() => {
    // Only change the league if a specific lineup is being shown. 
    // For selected but collapsed lineups the league shouldn't be changed. 
    // Only once the user enters the lineup can they make changes at which point the league needs to match.
    if (league && league !== filters.league && isEntryView) {
      dispatch(setLeagueFilter(Leagues[league.toUpperCase() as keyof typeof Leagues]));
    }
  }, [selectedEntry, isEntryView, dispatch]);

  return (
    <Box sx={{ boxSizing: 'border-box', width: '100%', height: '100%', background: '#1C1C1C', borderRadius: '10px', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: '15px', display: 'inline-flex' }}>
      {isEntryView &&
        (<Box sx={{ boxSizing: 'border-box', padding: '10px 10px', justifyContent: 'space-between', display: 'flex', width: '100%' }}>
          <Button style={{ background: 'rgba(255, 255, 255, 0.08)', height: '40px', color: theme.palette.common.white }} onClick={toggleView}>Lineup {(selectedEntry ?? 0) + 1} <ArrowDropDownIcon /></Button>
          <Stack gap={1} direction='row'>
            <Stack direction='column'>
              <Typography style={{ color: 'rgba(255, 255, 255, 0.87)', fontSize: 12 }}>xWIN</Typography>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.87)', fontSize: '18px' }}>{currentEntry?.outcomes?.length >= 2 && Math.round(displayData.expectedWinPct)}%</Typography>
            </Stack>
            <Stack direction='column'>
              <Typography style={{ color: 'rgba(255, 255, 255, 0.87)', alignSelf: 'center', fontSize: 12 }}>Odds</Typography>
              <Typography sx={{ fontSize: '18px', color: 'rgba(255, 255, 255, 0.87)' }}>{displayData.oddsAmerican}</Typography>
            </Stack>
          </Stack>
        </Box>)}
      <Box style={{ alignSelf: 'stretch', borderRadius: '10px', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', display: 'flex' }}>
        <Box style={{ alignSelf: 'stretch', flex: '1 1 0', flexDirection: 'column', display: 'flex' }}>
          {isEntryView && currentEntry?.outcomes?.map((leg) => (
            <EntryOutcome outcome={leg} />
          ))}
          {isEntryView && (!currentEntry?.outcomes || currentEntry?.outcomes?.length < 2) && (
            <BlankPlaceholder />
          )
          }
          {!isEntryView && entries?.map((entry, index) => (
            <EntryLineup entry={entry} />
          ))}
          {isEntryView && currentEntry?.outcomes &&
            (<Box sx={{ cursor: 'pointer', justifyContent: 'center', display: 'flex', padding: '5px 0px', color: '#FFA2AC', backgroundColor: '#33292A', fontFamily: 'Inter' }}>
              <a onClick={clearLineup}>CLEAR ALL PLAYS X</a>
            </Box>)}
          {isEntryView && <EstimatedPayouts wager={wager} payout={payout} />}
          {!isEntryView && <NewEntryButton onClick={newLineupClick} />}
          {!isEntryView && <GenerateLineup />}
        </Box>
      </Box>
    </Box>
    );
}

export default LineupSelector